import { Command, CommandGroup, CommandGroupSource, CommandQuery, Options } from "@omegagrid/commands";
import { msg } from "@omegagrid/localize";
import rp, { CommandProvider, formatDate } from "@reporting/rp-app-host";
import { AccountingSearchEntity } from '@reporting/rp-types/accounting';
import { css, html } from "lit";
import { AccountingCommandProps, accountingGroupId, buildAccountingGroups } from './searchGroups';

const itemStyle = css`
	.item .group-text {
		flex: 1;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.item .group-current {
		color: var(--og-accent-color);
		font-weight: bold;
		margin-left: 4px;
	}

	.item .group-count {
		flex: 0 0 auto;
		margin: 3px 15px 0 6px;
		padding: 0 6px;
		font-size: 0.8em;
		line-height: 14px;
		border-radius: 8px;
		border: 1px solid var(--og-border);
	}

	.item .command-visited {
		flex: 0 0 auto;
		margin-right: 6px;
		opacity: 0.6;
	}
`;

export class AccountingSearchProvider implements CommandProvider {

	id = 'accounting';
	title = msg('Companies');
	placeholder = msg('Search companies...');
	triggerCommand = 'a';

	/** the group of the current accounting: without a term the server lists it right after the recent companies */
	get currentGroupId() {
		const idGroup = rp.accountingProvider.accounting?.id_accounting_group;
		return idGroup == null ? null : accountingGroupId(idGroup);
	}

	getGroups = async (query?: CommandQuery): Promise<CommandGroupSource[]> => {
		const rows = await rp.ms('accounting').get<AccountingSearchEntity[]>('/search', {
			q: query?.filter,
			sort: rp.settings.get('accounting.search.sorting')
		});
		return buildAccountingGroups(rows, msg('Recent companies'), a => this.switchAccounting(a));
	}

	async switchAccounting(a: number) {
		if (a === rp.accountingProvider.a) return; // the company the user is in
		if (!await rp.host.confirmUnsavedChanges(msg('Discard changes and switch company?'))) return;
		rp.router.setState({a});
	}

	_onCommandPlusClick(e: MouseEvent, command: Command) {
		e.stopPropagation();
		e.preventDefault();
		const a = (command.props as AccountingCommandProps).accounting;
		window.open(rp.router.getUrl({a}));
	}

	getOptions = (): Options => ({
		itemStyle,
		groupRenderer: (_div: HTMLDivElement, group: CommandGroup) => html`
			<div class="group" @mousedown="${(e: MouseEvent) => e.preventDefault()}">
				<div class="group-toggle">
					<og-icon icon="angle-${group.open ? 'down' : 'right'}"></og-icon>
				</div>
				<div class="group-text" title="${group.name}">
					${group.name}
					${group.id === this.currentGroupId ? html`<span class="group-current">(${msg('Current group')})</span>` : ''}
				</div>
				<div class="group-count">${group.commands.size}</div>
			</div>
		`,
		commandRenderer: (div: HTMLDivElement, command: Command) => {
			div.style.height = '22px';
			div.style.lineHeight = '20px';
			const visited = (command.props as AccountingCommandProps)?.date_visited;
			return html`
				<div class="command" style="display: flex">
					<div style="flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${command.name}">
						${command.name}
					</div>
					${visited ? html`<div class="command-visited">${formatDate(visited)}</div>` : ''}
					<og-button
						icon="window-restore"
						color="transparent"
						title="${msg('Open in new tab')}"
						@mousedown="${(e: MouseEvent) => this._onCommandPlusClick(e, command)}"
						style="width: 20px; flex: 0 0 20px; margin-right: 15px">
					</og-button>
				</div>
			`;
		}
	});

}
